import { useMemo, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ApiError } from '../../shared/api/http'
import { useAuth } from './AuthContext'
import { LanguageSelector } from '../../shared/i18n/LanguageSelector'
import { useLocale } from '../../shared/i18n/LocaleContext'

const loginPattern = /^[A-Za-z0-9._-]{4,40}$/

export function RegisterPage() {
  const auth = useAuth()
  const { language, t } = useLocale()
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [legalData, setLegalData] = useState<Record<string, string> | null>(null)
  const [accepted, setAccepted] = useState(false)
  const requirements = useMemo(() => [
    [password.length >= 6, t('passwordLength')],
    [/[A-Z]/.test(password), t('passwordUpper')],
    [/[a-z]/.test(password), t('passwordLower')],
    [/\d/.test(password), t('passwordNumber')],
    [/[^A-Za-z0-9]/.test(password), t('passwordSymbol')],
  ] as const, [password, t])

  function validate(data: FormData) {
    const login = String(data.get('login')).trim()
    const birthDate = String(data.get('birthDate'))
    if (!loginPattern.test(login)) return t('invalidLogin')
    if (!birthDate || new Date(`${birthDate}T00:00:00`) >= new Date()) return t('invalidBirthDate')
    if (requirements.some(([met]) => !met)) return t('passwordRequirementsMissing')
    if (password !== confirmation) return t('passwordMismatch')
    return null
  }

  function requestConfirmation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    const validation = validate(data)
    if (validation) { setError(validation); return }
    setError('')
    setAccepted(false)
    setLegalData(Object.fromEntries([...data.entries()].map(([key, value]) => [key, String(value)])))
  }

  async function register() {
    if (!legalData || !accepted) return
    setBusy(true)
    setError('')
    try {
      await auth.register({ login: legalData.login.trim(), fullName: legalData.fullName.trim(), birthDate: legalData.birthDate, email: legalData.email.trim(), phone: legalData.phone.trim(), password, passwordConfirmation: confirmation, language })
      navigate('/')
    } catch (cause) {
      const code = cause instanceof ApiError ? cause.code : ''
      setError(code === 'login_unavailable' ? t('loginUnavailable') : code === 'email_unavailable' ? t('emailUnavailable') : code === 'weak_password' ? t('weakPassword') : t('registerError'))
      setLegalData(null)
    } finally { setBusy(false) }
  }

  return <div className="register-page">
    <section className="register-copy"><img src="/logo.png" alt="Spectra Guard AI"/><LanguageSelector compact/><span className="eyebrow">{t('newAccess')}</span><h1>{t('registerHero')}</h1><p>{t('registerHeroText')}</p></section>
    <form className="register-form" onSubmit={requestConfirmation}>
      <div className="register-heading"><div><h2>{t('personalData')}</h2><p>Sua conta começa com acesso ao Remote. Vigilância e Portaria dependem de liberação do administrador.</p></div><Link to="/login">{t('alreadyAccount')}</Link></div>
      <div className="form-grid">
        <label>{t('login')}<input name="login" autoComplete="username" minLength={4} maxLength={40} pattern="[A-Za-z0-9._-]{4,40}" required/></label>
        <label>{t('fullName')}<input name="fullName" autoComplete="name" required/></label>
        <label>{t('birthDate')}<input name="birthDate" type="date" max={new Date(Date.now()-86400000).toISOString().slice(0,10)} required/></label>
        <label>{t('email')}<input name="email" type="email" autoComplete="email" required/></label>
        <label>{t('phone')}<input name="phone" type="tel" autoComplete="tel" required/></label><span/>
        <label>{t('password')}<span className="password-field"><input name="password" type={showPassword?'text':'password'} autoComplete="new-password" value={password} onChange={event=>setPassword(event.target.value)} required/><button type="button" aria-label={showPassword?t('hidePassword'):t('showPassword')} onClick={()=>setShowPassword(value=>!value)}>{showPassword?'◉':'◎'}</button></span></label>
        <label>{t('confirmPassword')}<input name="passwordConfirmation" type={showPassword?'text':'password'} autoComplete="new-password" value={confirmation} onChange={event=>setConfirmation(event.target.value)} required/></label>
      </div>
      <div className="password-requirements">{requirements.map(([met,label])=><span className={met?'met':''} key={label}><i>{met?'✓':'○'}</i>{label}</span>)}</div>
      {error&&<div className="form-error" role="alert">{error}</div>}
      <button className="primary" disabled={busy}>{busy?t('creatingAccount'):t('createFree')}</button>
      <p className="register-signin">{t('alreadyHaveAccount')} <Link to="/login">{t('signInNow')}</Link></p>
    </form>
    {legalData&&<div className="legal-confirmation-backdrop"><section className="legal-confirmation" role="dialog" aria-modal="true" aria-labelledby="legal-title"><header><div><span className="eyebrow blue">{t('legalConfirmation')}</span><h2 id="legal-title">{t('termsAndPrivacy')}</h2></div><button type="button" aria-label={t('close')} onClick={()=>setLegalData(null)}>×</button></header><p>{t('legalIntro')}</p><div className="legal-document-links"><Link to="/termos" target="_blank">{t('openTerms')}</Link><Link to="/privacidade" target="_blank">{t('openPrivacy')}</Link></div><label className="terms-check"><input type="checkbox" checked={accepted} onChange={event=>setAccepted(event.target.checked)}/><span>{t('acceptLegal')}</span></label><footer><button type="button" onClick={()=>setLegalData(null)}>{t('back')}</button><button type="button" className="compact-primary" disabled={!accepted||busy} onClick={()=>void register()}>{busy?t('creating'):t('confirmCreate')}</button></footer></section></div>}
  </div>
}
